import React from "react";
import PropTypes from "prop-types";
import { Typography } from "@mui/material";

import { UINumberField } from "../../../../ui";
import { clx } from "../../../../../utils/Helpers";
import {
  CUSTOM_CREW_SIZE_MIN,
  CUSTOM_PLAN_ERRORS,
  fillTemplate,
  formatAmount,
} from "./crewSizeUtils";

/** Custom crew size and rate agreed with the company for more than 300 applicants. */
const CustomCrewSize = ({
  containerRef,
  t,
  plan,
  state,
  serverErrors,
  selected,
  extended,
  disabled,
  disabledHint,
  onChange,
}) => {
  const errorMessages = {
    [CUSTOM_PLAN_ERRORS.required]: t.required,
    [CUSTOM_PLAN_ERRORS.belowMinimum]: t.belowMinimum,
    [CUSTOM_PLAN_ERRORS.invalidRate]: t.invalidRate,
  };
  const minimumHint = fillTemplate(t.minimumHint, {
    min: formatAmount(CUSTOM_CREW_SIZE_MIN),
  });
  const applicantsError =
    errorMessages[state.applicantsError] ?? serverErrors.applicants;
  const rateError = errorMessages[state.rateError] ?? serverErrors.rate;

  return (
    <section
      ref={containerRef}
      className={clx("crew-size-step__custom", {
        "crew-size-step__custom--selected": selected,
      })}
    >
      <div>
        <Typography component="h2" className="text-heading-sm crew-size-step__custom-title">
          {t.title}
        </Typography>
        <Typography component="p" className="text-body-sm crew-size-step__text">
          {(disabled && disabledHint) || t.subtitle}
        </Typography>
      </div>

      <div className="crew-size-step__custom-fields">
        <UINumberField
          id="custom-crew-size-applicants"
          name="custom_crew_size_applicants"
          label={t.applicants}
          placeholder={formatAmount(CUSTOM_CREW_SIZE_MIN)}
          required
          min={CUSTOM_CREW_SIZE_MIN}
          step={1}
          disabled={disabled}
          value={plan.applicants}
          onValueChange={(applicants) => onChange({ applicants })}
          error={Boolean(applicantsError)}
          helperText={applicantsError ?? minimumHint}
        />
        <UINumberField
          id="custom-crew-size-rate"
          name="custom_crew_size_rate"
          label={extended ? t.eventRate : t.ratePerDay}
          placeholder="0"
          required
          min={0}
          disabled={disabled}
          value={plan.rate}
          onValueChange={(rate) => onChange({ rate })}
          error={Boolean(rateError)}
          helperText={rateError}
        />
      </div>
    </section>
  );
};

CustomCrewSize.propTypes = {
  /** The section, scrolled to when "Next" is pressed with the fields incomplete. */
  containerRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  /** `events.creatEvent.crewSize.custom` copy. */
  t: PropTypes.object.isRequired,
  plan: PropTypes.shape({
    applicants: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    rate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  /** Result of `getCustomCrewPlanState(plan)`. */
  state: PropTypes.shape({
    applicantsError: PropTypes.string,
    rateError: PropTypes.string,
  }).isRequired,
  /** Messages the backend returned on the last save, by field. */
  serverErrors: PropTypes.shape({
    applicants: PropTypes.string,
    rate: PropTypes.string,
  }),
  selected: PropTypes.bool,
  /** Extended events take one flat "Event Rate" instead of a rate per day. */
  extended: PropTypes.bool,
  /** Read-only for the company once a custom plan applies to the event. */
  disabled: PropTypes.bool,
  /** Why the fields are read-only, shown in place of the subtitle. */
  disabledHint: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

CustomCrewSize.defaultProps = {
  containerRef: undefined,
  serverErrors: {},
  selected: false,
  extended: false,
  disabled: false,
  disabledHint: undefined,
};

export default CustomCrewSize;
